import {
  checksForLine,
  headerChecks,
  isBlockingOutcome,
  issueLineLabel,
  lineBlockedFromAccept,
  type Check,
} from '@/lib/validation'
import type { Order, ValidationIssue } from '@/lib/vision/schemas'

/**
 * What Acknowledge is about to do, split the way Vision's `LineAckPlanner` splits it:
 * a line carrying a blocking check goes to Amazon as **Rejected at full ordered qty**,
 * every other line Accepts with the operator's qty.
 *
 * Pulse used to derive only the Accept half — the request payload — by filtering the
 * blocked lines out. The Rejects were real, went to Amazon, and were never counted or
 * shown anywhere before the submit. One function now produces both halves so the
 * confirmation cannot describe a submission different from the one it sends.
 */

export interface AckLineWire {
  itemSequenceNumber: number
  acknowledgedQuantity: number
}

export interface RejectedLine {
  itemSequenceNumber: number
  label: string
  units: number
  checks: readonly Check[]
}

export interface AckPlan {
  lines: AckLineWire[]
  acceptedLines: number
  acceptedUnits: number
  rejected: readonly RejectedLine[]
  rejectedUnits: number
  /** Blocking header failures, which block every line at once. */
  headerBlocks: readonly Check[]
  allRejected: boolean
}

export function planAcknowledgement(
  order: Order,
  issues: readonly ValidationIssue[],
  qtyBySeq: Record<number, number>,
): AckPlan {
  const lines: AckLineWire[] = []
  const rejected: RejectedLine[] = []
  let acceptedUnits = 0
  let rejectedUnits = 0

  for (const item of order.items) {
    const ordered = Number(item.orderedQuantity ?? 0)
    if (lineBlockedFromAccept(item, issues, order.items)) {
      rejected.push({
        itemSequenceNumber: item.itemSequenceNumber,
        label: issueLineLabel(item),
        units: ordered,
        checks: checksForLine(item, issues, order.items).filter(check =>
          isBlockingOutcome(check.outcome),
        ),
      })
      rejectedUnits += ordered
      continue
    }
    const qty = Math.min(Math.max(0, qtyBySeq[item.itemSequenceNumber] ?? ordered), ordered)
    lines.push({ itemSequenceNumber: item.itemSequenceNumber, acknowledgedQuantity: qty })
    acceptedUnits += qty
  }

  return {
    lines,
    acceptedLines: lines.length,
    acceptedUnits,
    rejected,
    rejectedUnits,
    headerBlocks: headerChecks(issues).filter(check => isBlockingOutcome(check.outcome)),
    allRejected: rejected.length > 0 && lines.length === 0,
  }
}

/** What the toast says once the operator has agreed to it. */
export function ackOutcomeMessage(plan: AckPlan): string {
  const accepted = `${plan.acceptedLines} ${plan.acceptedLines === 1 ? 'line' : 'lines'} accepted`
  if (plan.rejected.length === 0) return `Acknowledged — ${accepted}`
  const rejected = `${plan.rejected.length} ${plan.rejected.length === 1 ? 'line' : 'lines'} rejected`
  return plan.allRejected ? `Acknowledged — ${rejected}` : `Acknowledged — ${rejected}, ${accepted}`
}
